import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography,
} from '@mui/material';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { FixedAssetService } from '../../services/fixedAsset.service';
import { FixedAssetSettingsService } from '../../services/fixedAssetSettings.service';
import { FixedAssetCategoryService } from '../../services/fixedAssetCategory.service';
import { FixedAssetCategory } from '../../interfaces/fixedAsset/cwip.interface';
import { DepreciationMethod } from '../../interfaces/fixedAsset/fixedAsset.interface';
import SearchableSelect from '../common/SearchableSelect';
import { AccountService } from '../../services/account.service';
import { postingAccounts } from '../../utils/accountHierarchy';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { SegmentService } from '../../services/segment.service';
import { Account, Segment } from '../../interfaces/accounting.interface';
import { FixedAssetSettings } from '../../interfaces/fixedAsset/fixedAssetSettings.interface';
import { toLocalIsoDate } from '../../utils/dateUtils';
// ---- F10 placement ----
import CustodianPicker from './CustodianPicker';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { CostCentreService } from '../../services/costCentre.service';
import { AssetLocation } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import { DepartmentOption } from '../../interfaces/costCentre.interface';
// ---- end F10 ----
import {
  categoryTextAfterPick, defaultDepreciatedThrough, lifeAfterCategoryPick, lifeIsSet, startLongBeforeOpening,
} from './fixedAssetFormRules';

type Mode = 'newPurchase' | 'register' | 'edit';

const today = () => toLocalIsoDate();

const EMPTY = {
  name: '', description: '', category: '',
  acquisitionDate: today(), acquisitionCost: '', salvageValue: '0',
  usefulLifeMonths: '', depreciationStartDate: today(), segmentId: '',
  assetAccountId: '', accumulatedDepreciationAccountId: '', depreciationExpenseAccountId: '',
  creditAccountId: '',
  // W3-K
  categoryId: '', depreciationMethod: 'STRAIGHT_LINE' as DepreciationMethod, depreciationRatePct: '',
  openingDate: today(), openingAccumulatedDepreciation: '0', depreciatedThrough: '',
  // F10: optional initial placement (new purchase only; afterwards it changes through a transfer)
  locationId: '', departmentId: '', custodianUserId: '',
};

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FixedAssetForm: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const mode: Mode = id ? 'edit' : (location.pathname.endsWith('/register') ? 'register' : 'newPurchase');

  // GL accounts of an asset are accounting setup (accounts team only); others see them read-only, from the category.
  const canSetAccounts = usePermission(PERMISSIONS.FIXED_ASSET_SETTINGS_MANAGE);
  const [form, setForm] = useState({ ...EMPTY });
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [settings, setSettings] = useState<FixedAssetSettings | null>(null);
  const [categories, setCategories] = useState<FixedAssetCategory[]>([]);   // W3-K
  const [depreciatedThroughLocked, setDepreciatedThroughLocked] = useState(false);
  // edit mode: the asset's accounts as loaded, and whether journals have already posted to them
  const [original, setOriginal] = useState<{
    assetAccountId: string; accumulatedDepreciationAccountId: string; depreciationExpenseAccountId: string;
    hasPostedHistory: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // ---- F10 ----
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  useEffect(() => {
    if (mode !== 'newPurchase') return;
    FixedAssetTransferService.locations().then(r => setLocations(Array.isArray(r) ? r.filter(l => l.active) : []))
      .catch(e => { setLocations([]); toast.showApiError(e, 'Failed to load asset locations'); });
    CostCentreService.getDepartments().then(r => setDepartments(Array.isArray(r) ? r : []))
      .catch(e => { setDepartments([]); toast.showApiError(e, 'Failed to load departments'); });
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps
  // ---- end F10 ----

  useEffect(() => {
    const institutionId = AuthService.getSelectedInstitution()!;
    (async () => {
      try {
        setLoading(true);
        const [settingsRes, accountsRes, segmentsRes, categoriesRes] = await Promise.all([
          FixedAssetSettingsService.getByInstitution(institutionId).catch(() => null),
          AccountService.getLookup(),
          SegmentService.getByInstitution(0, 200).catch(() => ({ content: [] })),
          FixedAssetCategoryService.list(institutionId).catch(() => [] as FixedAssetCategory[]),
        ]);
        setCategories(Array.isArray(categoriesRes) ? categoriesRes.filter(c => c.active) : []);
        setSettings(settingsRes);
        const allAccounts: Account[] = accountsRes ?? [];
        setAccounts(allAccounts);
        setSegments(segmentsRes?.content ?? []);
        if (id) {
          const a = await FixedAssetService.getById(id);
          // A group (header) account on the asset cannot be kept: leave that picker empty so the
          // user must choose a sub-account before saving.
          const leafIds = new Set(postingAccounts(allAccounts).map(x => x.id));
          const keepLeaf = (accountId?: string) => (accountId && leafIds.has(accountId) ? accountId : '');
          setForm({
            ...EMPTY,
            name: a.name, description: a.description ?? '', category: a.category ?? '',
            acquisitionDate: a.acquisitionDate, acquisitionCost: String(a.acquisitionCost),
            salvageValue: String(a.salvageValue), usefulLifeMonths: String(a.usefulLifeMonths),
            depreciationStartDate: a.depreciationStartDate, segmentId: a.segmentId ?? '',
            assetAccountId: keepLeaf(a.assetAccountId),
            accumulatedDepreciationAccountId: keepLeaf(a.accumulatedDepreciationAccountId),
            depreciationExpenseAccountId: keepLeaf(a.depreciationExpenseAccountId),
            categoryId: a.categoryId ?? '',
            depreciationMethod: a.depreciationMethod ?? 'STRAIGHT_LINE',
            depreciationRatePct: a.depreciationRatePct != null ? String(a.depreciationRatePct) : '',
          });
          setOriginal({
            assetAccountId: a.assetAccountId ?? '',
            accumulatedDepreciationAccountId: a.accumulatedDepreciationAccountId ?? '',
            depreciationExpenseAccountId: a.depreciationExpenseAccountId ?? '',
            hasPostedHistory: !!a.acquisitionJournalEntryId || !!a.depreciatedThrough,
          });
          setDepreciatedThroughLocked(!!a.depreciatedThrough);
        }
      } catch (e) {
        toast.showApiError(e, 'Failed to load form data');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const setAccount = (k: keyof typeof EMPTY) => (accountId: string) =>
    setForm(f => ({ ...f, [k]: accountId }));

  // Group (header) accounts only carry their sub-accounts' totals, so only leaf accounts are offered.
  const leafAccounts = useMemo(() => postingAccounts(accounts), [accounts]);
  const assetAccounts = leafAccounts.filter(a => a.accountType === 'ASSET');
  const expenseAccounts = leafAccounts.filter(a => a.accountType === 'EXPENSE');
  const creditAccounts = leafAccounts.filter(a => a.accountType === 'ASSET' || a.accountType === 'LIABILITY');

  // What will actually be used: the override, else the settings default (create/register only;
  // an edit always names the asset's own accounts).
  const isEdit = mode === 'edit';
  const effectiveAssetId = form.assetAccountId || (isEdit ? '' : settings?.defaultAssetAccountId) || '';
  const effectiveAccumId = form.accumulatedDepreciationAccountId
    || (isEdit ? '' : settings?.defaultAccumulatedDepreciationAccountId) || '';
  const effectiveExpenseId = form.depreciationExpenseAccountId
    || (isEdit ? '' : settings?.defaultDepreciationExpenseAccountId) || '';
  const sameAssetAndAccum = !!effectiveAssetId && effectiveAssetId === effectiveAccumId;
  const sameAssetAndExpense = !!effectiveAssetId && effectiveAssetId === effectiveExpenseId;
  const accountConflict = canSetAccounts && (sameAssetAndAccum || sameAssetAndExpense);

  // edit: accounts the asset currently points at that are group (header) accounts
  const leafIdSet = new Set(leafAccounts.map(a => a.id));
  const groupAccountsOnAsset = isEdit && original
    ? [original.assetAccountId, original.accumulatedDepreciationAccountId, original.depreciationExpenseAccountId]
      .filter(accountId => !!accountId && !leafIdSet.has(accountId))
      .map(accountId => accounts.find(a => a.id === accountId))
      .filter((a): a is Account => !!a)
    : [];
  const accountsChanged = isEdit && !!original && (
    form.assetAccountId !== original.assetAccountId
    || form.accumulatedDepreciationAccountId !== original.accumulatedDepreciationAccountId
    || form.depreciationExpenseAccountId !== original.depreciationExpenseAccountId);
  const missingEditAccount = isEdit && canSetAccounts
    && (!form.assetAccountId || !form.accumulatedDepreciationAccountId || !form.depreciationExpenseAccountId);

  const defaultLabel = (accountId?: string) => {
    if (!accountId) return 'None';
    const a = accounts.find(x => x.id === accountId);
    return a ? `Default: ${a.code} - ${a.name}` : 'Default';
  };

  const cost = parseFloat(form.acquisitionCost) || 0;
  const salvage = parseFloat(form.salvageValue) || 0;
  const remaining = parseInt(form.usefulLifeMonths, 10) || 0;
  const openingAccum = parseFloat(form.openingAccumulatedDepreciation) || 0;

  // Polish 16: carried-in depreciation requires a "through" marker; default = month before the opening date
  const effectiveDepreciatedThrough = form.depreciatedThrough
    || (openingAccum > 0 ? defaultDepreciatedThrough(form.openingDate) : '');
  const startFarBefore = mode === 'register' && openingAccum > 0
    && startLongBeforeOpening(form.depreciationStartDate, form.openingDate);

  const preview = useMemo(() => {
    if (mode !== 'register' || cost <= 0 || remaining <= 0) return null;
    const openingNbv = cost - openingAccum;
    const monthly = (openingNbv - salvage) / remaining;
    return { openingNbv, monthly };
  }, [mode, cost, salvage, remaining, openingAccum]);

  // W3-K: reducing balance needs a rate instead of a life; a category may supply the life.
  const reducing = form.depreciationMethod === 'REDUCING_BALANCE';
  const chosenCategory = categories.find(c => c.id === form.categoryId);
  const lifeOk = reducing || lifeIsSet(mode, remaining, chosenCategory?.lifeMonths);
  const rateOk = !reducing || parseFloat(form.depreciationRatePct) > 0;

  const pickCategory = (categoryId: string, c: FixedAssetCategory | null) => setForm(f => ({
    ...f,
    categoryId,
    // Polish 21: on edit only the link (and its label) change; terms of a live asset are not re-defaulted
    ...(c && isEdit ? { category: categoryTextAfterPick(f.category, c.name, true) } : {}),
    ...(c && !isEdit ? {
      category: categoryTextAfterPick(f.category, c.name, false),
      depreciationMethod: c.defaultMethod,
      usefulLifeMonths: lifeAfterCategoryPick(mode, f.usefulLifeMonths, c.lifeMonths),
      depreciationRatePct: c.ratePct != null ? String(c.ratePct) : f.depreciationRatePct,
      salvageValue: (parseFloat(f.acquisitionCost) || 0) > 0
        ? String(Math.round((parseFloat(f.acquisitionCost) * (c.salvagePct ?? 0)) ) / 100) : f.salvageValue,
    } : {}),
  }));

  const invalid =
    accountConflict ||
    missingEditAccount ||
    !form.name.trim() ||
    !rateOk ||
    (mode !== 'edit' && (cost <= 0 || !lifeOk)) ||
    (mode === 'newPurchase' && (canSetAccounts ? !form.creditAccountId : !settings?.defaultFundingAccountId)) ||
    (mode === 'register' && !form.openingDate);

  const submit = async () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    try {
      setSaving(true);
      if (mode === 'edit' && id) {
        await FixedAssetService.update(id, {
          name: form.name.trim(),
          description: form.description || undefined,
          category: form.category || undefined,
          // without SETTINGS_MANAGE the asset's own accounts go back exactly as loaded (a change would be a 403)
          assetAccountId: canSetAccounts ? form.assetAccountId : original?.assetAccountId ?? '',
          accumulatedDepreciationAccountId: canSetAccounts ? form.accumulatedDepreciationAccountId : original?.accumulatedDepreciationAccountId ?? '',
          depreciationExpenseAccountId: canSetAccounts ? form.depreciationExpenseAccountId : original?.depreciationExpenseAccountId ?? '',
          categoryId: form.categoryId || undefined,
          depreciationMethod: form.depreciationMethod,
          depreciationRatePct: reducing ? parseFloat(form.depreciationRatePct) : undefined,
        });
        toast.showSuccess('Asset updated');
        navigate(`/fixed-assets/${id}`);
        return;
      }
      const shared = {
        institutionId, name: form.name.trim(),
        description: form.description || undefined,
        category: form.category || undefined,
        acquisitionDate: form.acquisitionDate,
        acquisitionCost: cost, salvageValue: salvage,
        usefulLifeMonths: remaining, depreciationStartDate: form.depreciationStartDate,
        segmentId: form.segmentId || undefined,
        ...(canSetAccounts ? {
          assetAccountId: form.assetAccountId || undefined,
          accumulatedDepreciationAccountId: form.accumulatedDepreciationAccountId || undefined,
          depreciationExpenseAccountId: form.depreciationExpenseAccountId || undefined,
        } : {}),
        categoryId: form.categoryId || undefined,
        depreciationMethod: form.depreciationMethod,
        depreciationRatePct: reducing ? parseFloat(form.depreciationRatePct) : undefined,
      };
      let created;
      if (mode === 'newPurchase') {
        created = await FixedAssetService.create({
          ...shared, creditAccountId: canSetAccounts ? form.creditAccountId : undefined,
          locationId: form.locationId || undefined, // F10
          departmentId: form.departmentId || undefined,
          custodianUserId: form.custodianUserId || undefined,
        });
      } else {
        created = await FixedAssetService.register({
          ...shared,
          openingDate: form.openingDate,
          openingAccumulatedDepreciation: openingAccum,
          depreciatedThrough: effectiveDepreciatedThrough || undefined,
        });
      }
      toast.showSuccess(mode === 'newPurchase' ? 'Asset created' : 'Asset registered');
      navigate(`/fixed-assets/${created.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to save asset');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  const title = mode === 'edit' ? 'Edit Asset'
    : mode === 'register' ? 'Register Existing Asset' : 'New Asset (Purchase)';

  return (
    <Box sx={{ p: 3, maxWidth: 820 }} data-help-page="fixedAssets.form.page">
      <Typography variant="h5" sx={{ mb: 2 }}>{title}</Typography>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField data-help="fixedAssets.form.field.name" label="Name" required value={form.name} onChange={set('name')} fullWidth />
        <TextField data-help="fixedAssets.form.field.description" label="Description" value={form.description} onChange={set('description')} fullWidth />
        {/* ---- W3-K: asset category (defaults) and depreciation method ---- */}
        <SearchableSelect<FixedAssetCategory> data-help="fixedAssets.form.field.assetCategory"
          options={categories} value={form.categoryId} label="Asset category (pre-fills method, life, accounts)"
          getOptionLabel={c => c.name} onChange={pickCategory} data-testid="asset-category" />
        <TextField data-help="fixedAssets.form.field.categoryText" label={form.categoryId ? 'Category (free text)' : 'Category'} value={form.category}
          onChange={set('category')} fullWidth
          helperText={!form.categoryId && form.category ? 'Shown while no category is chosen; "Map categories" can link it later.' : undefined} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField data-help="fixedAssets.form.field.depreciationMethod" select label="Depreciation method" value={form.depreciationMethod}
            sx={{ flex: '1 1 220px', maxWidth: 320 }}
            disabled={isEdit && depreciatedThroughLocked}
            helperText={isEdit && depreciatedThroughLocked ? 'Cannot change once depreciation has been posted' : undefined}
            onChange={e => setForm(f => ({ ...f, depreciationMethod: e.target.value as DepreciationMethod }))}>
            <MenuItem value="STRAIGHT_LINE">Straight line</MenuItem>
            <MenuItem value="REDUCING_BALANCE">Reducing balance</MenuItem>
          </TextField>
          {reducing && (
            <TextField data-help="fixedAssets.form.field.ratePct" label="Rate % per year" type="number" required value={form.depreciationRatePct}
              sx={{ flex: '1 1 160px', maxWidth: 240 }} onChange={set('depreciationRatePct')}
              helperText="Monthly charge = book value at month start x rate / 12, never below salvage" />
          )}
        </Box>
        {/* ---- end W3-K ---- */}

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <TextField data-help="fixedAssets.form.field.acquisitionDate" label="Acquisition date" type="date" InputLabelProps={{ shrink: true }}
            value={form.acquisitionDate} onChange={set('acquisitionDate')} fullWidth
            disabled={mode === 'edit'} />
          <TextField data-help="fixedAssets.form.field.depreciationStartDate" label="Depreciation start date" type="date" InputLabelProps={{ shrink: true }}
            value={form.depreciationStartDate} onChange={set('depreciationStartDate')} fullWidth
            disabled={mode === 'edit'} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
          <TextField data-help="fixedAssets.form.field.acquisitionCost" label="Acquisition cost" type="number" value={form.acquisitionCost}
            onChange={set('acquisitionCost')} fullWidth disabled={mode === 'edit'} />
          <TextField data-help="fixedAssets.form.field.salvageValue" label="Salvage value" type="number" value={form.salvageValue}
            onChange={set('salvageValue')} fullWidth disabled={mode === 'edit'} />
          <TextField data-help="fixedAssets.form.field.usefulLife" label={mode === 'register' ? 'Remaining life (months)' : 'Useful life (months)'}
            type="number" value={form.usefulLifeMonths} onChange={set('usefulLifeMonths')} fullWidth
            disabled={mode === 'edit'}
            helperText={mode === 'register' && chosenCategory?.lifeMonths
              ? `Months left, not the full life (category life is ${chosenCategory.lifeMonths})` : undefined} />
        </Box>

        {mode === 'edit' && (
          <Alert severity="info">
            Cost, useful life, and salvage are not editable after creation. To revise them, dispose the
            asset and re-register it (a disposal loss may result — see the asset detail help).
          </Alert>
        )}

        <TextField data-help="fixedAssets.form.field.segment" select label="Segment (optional)" value={form.segmentId} onChange={set('segmentId')}
          fullWidth disabled={mode === 'edit'}>
          <MenuItem value=""><em>Common (none)</em></MenuItem>
          {segments.map(s => <MenuItem key={s.id} value={s.id}>{s.code} - {s.name}</MenuItem>)}
        </TextField>

        {mode === 'newPurchase' && (
          <Box data-help-section="fixedAssets.form.section.placement" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>{/* ---- F10 placement (optional) ---- */}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Placement (optional)</Typography>
            <SearchableSelect<AssetLocation>
              data-help="fixedAssets.form.field.location" label="Location" options={locations} value={form.locationId}
              onChange={v => setForm(f => ({ ...f, locationId: v }))}
              getOptionLabel={l => `${l.code} - ${l.name}`} getOptionId={l => l.id} />
            <SearchableSelect<DepartmentOption>
              data-help="fixedAssets.form.field.department" label="Department" options={departments} value={form.departmentId}
              onChange={v => setForm(f => ({ ...f, departmentId: v }))}
              getOptionLabel={d => d.name} getOptionId={d => d.id} />
            <CustodianPicker data-help="fixedAssets.form.field.custodian" value={form.custodianUserId}
              onChange={v => setForm(f => ({ ...f, custodianUserId: v }))} />
          </Box>
        )}

        {isEdit && groupAccountsOnAsset.length > 0 && (
          <Alert severity="error" data-testid="group-account-alert">
            This asset posts to group account{groupAccountsOnAsset.length > 1 ? 's' : ''}{' '}
            {groupAccountsOnAsset.map(a => `${a.code} - ${a.name}`).join(', ')}. A group account only
            carries its sub-accounts' totals, so depreciation for this asset is skipped until you pick a
            sub-account below.
          </Alert>
        )}

        {!canSetAccounts && (
          <Box data-help-section="fixedAssets.form.section.accounts" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Accounts</Typography>
            {(() => {
              const cat = categories.find(c => c.id === form.categoryId);
              const name = (accountId?: string) => {
                const x = accounts.find(y => y.id === accountId);
                return x ? `${x.code} - ${x.name}` : undefined;
              };
              const assetName = isEdit ? name(original?.assetAccountId) : name(cat?.assetAccountId ?? settings?.defaultAssetAccountId);
              const accumName = isEdit ? name(original?.accumulatedDepreciationAccountId)
                : name(cat?.accumulatedAccountId ?? settings?.defaultAccumulatedDepreciationAccountId);
              const expName = isEdit ? name(original?.depreciationExpenseAccountId)
                : name(cat?.expenseAccountId ?? settings?.defaultDepreciationExpenseAccountId);
              return assetName && accumName && expName ? (
                <Typography variant="body2" color="text.secondary" data-testid="readonly-accounts">
                  Posts to {assetName}; depreciation {expName} / {accumName}. Set by the accounts team.
                </Typography>
              ) : (
                <Alert severity="warning" data-testid="accounts-not-set">
                  Accounts not set - ask accounts. This asset cannot be posted until its category has accounts.
                </Alert>
              );
            })()}
          </Box>
        )}
        {canSetAccounts && (
        <Box data-help-section="fixedAssets.form.section.accounts" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {isEdit ? 'Accounts' : 'Account overrides (blank = use settings default)'}
          </Typography>
          <AccountAutocomplete
            accounts={assetAccounts}
            data-help="fixedAssets.form.field.assetAccount"
            value={form.assetAccountId}
            onChange={setAccount('assetAccountId')}
            label="Asset account"
            required={isEdit}
            placeholder={isEdit ? 'Select' : defaultLabel(settings?.defaultAssetAccountId)}
            error={accountConflict}
            helperText="Where the asset's cost is recorded. Must differ from the accumulated-depreciation and expense accounts."
          />
          <AccountAutocomplete
            accounts={assetAccounts}
            data-help="fixedAssets.form.field.accumulatedAccount"
            value={form.accumulatedDepreciationAccountId}
            onChange={setAccount('accumulatedDepreciationAccountId')}
            label="Accumulated-depreciation account"
            required={isEdit}
            placeholder={isEdit ? 'Select' : defaultLabel(settings?.defaultAccumulatedDepreciationAccountId)}
            error={sameAssetAndAccum}
            helperText={sameAssetAndAccum
              ? 'This is the same account as the asset account. Choose a separate accumulated-depreciation account.'
              : 'Holds the depreciation charged so far (reduces the asset value).'}
          />
          <AccountAutocomplete
            accounts={expenseAccounts}
            data-help="fixedAssets.form.field.expenseAccount"
            value={form.depreciationExpenseAccountId}
            onChange={setAccount('depreciationExpenseAccountId')}
            label="Depreciation-expense account"
            required={isEdit}
            placeholder={isEdit ? 'Select' : defaultLabel(settings?.defaultDepreciationExpenseAccountId)}
            error={sameAssetAndExpense}
            helperText={sameAssetAndExpense ? 'This is the same account as the asset account.' : undefined}
          />
          {accountsChanged && original?.hasPostedHistory && (
            <Alert severity="warning" data-testid="account-change-warning">
              New accounts apply to future postings only (depreciation runs and disposal). Past postings
              stay on the old accounts and are not moved automatically, so the disposal will clear this
              asset's full cost and depreciation from the new accounts. Have your accountant move the old
              balances first (Accounting → Group Account Balances for a group account, otherwise a journal
              entry) before disposing of the asset.
            </Alert>
          )}
        </Box>
        )}

        {mode === 'newPurchase' && !canSetAccounts && (
          settings?.defaultFundingAccountId ? (
            <Alert severity="info" data-testid="funding-account-default">
              Paid from: {accounts.find(a => a.id === settings.defaultFundingAccountId)?.name ?? 'the default funding account'}
              {' '}(set by your accountant)
            </Alert>
          ) : (
            <Alert severity="warning" data-testid="funding-account-missing">
              Ask the accountant to set the default funding account in Fixed asset settings before buying an asset.
            </Alert>
          )
        )}

        {mode === 'newPurchase' && canSetAccounts && (
          <AccountAutocomplete
            accounts={creditAccounts}
            data-help="fixedAssets.form.field.creditAccount"
            value={form.creditAccountId}
            onChange={setAccount('creditAccountId')}
            label="Funded by (credit account)"
            required
            placeholder="Select"
            helperText="Cash / payable / inventory account credited for the acquisition"
          />
        )}

        {mode === 'register' && (
          <Box data-help-section="fixedAssets.form.section.opening" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField data-help="fixedAssets.form.field.priorAccumulated" label="Prior accumulated depreciation" type="number"
                value={form.openingAccumulatedDepreciation}
                onChange={set('openingAccumulatedDepreciation')} fullWidth />
              <TextField data-help="fixedAssets.form.field.openingDate" label="Opening date (go-live)" type="date" InputLabelProps={{ shrink: true }}
                value={form.openingDate} onChange={set('openingDate')} fullWidth
                helperText="Date the opening JE posts — must be in an open period" />
              <TextField data-help="fixedAssets.form.field.depreciatedThrough" label="Depreciated through" type="date" InputLabelProps={{ shrink: true }}
                value={effectiveDepreciatedThrough} onChange={set('depreciatedThrough')} fullWidth
                disabled={depreciatedThroughLocked}
                helperText={openingAccum > 0
                  ? 'Months up to here are already in the prior accumulated depreciation. Defaults to the month of the opening date; depreciation then resumes the month after it.'
                  : 'Optional. Leave blank if no depreciation has been charged yet.'} />
            </Box>
            {startFarBefore && (
              <Alert severity="warning" data-testid="start-far-before-opening">
                The depreciation start date is long before the opening date. The history up to the opening date
                is NOT re-posted: it is carried in by "Prior accumulated depreciation". Depreciation resumes
                after the opening month.
              </Alert>
            )}
            {preview && (
              <Alert severity="info">
                Opening NBV = cost − prior accumulated = <b>{money(preview.openingNbv)}</b> ·
                going-forward monthly = (opening NBV − salvage) / remaining months = <b>{money(preview.monthly)}</b>
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary">
              An opening journal entry is posted on the opening date; carried-in balances are recorded on
              trust — verify via the tie-out report.
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button data-help="fixedAssets.form.cancel" onClick={() => navigate(id ? `/fixed-assets/${id}` : '/fixed-assets')}>Cancel</Button>
          <Button data-help="fixedAssets.form.save" variant="contained" onClick={submit} disabled={invalid || saving}>
            {mode === 'edit' ? 'Save' : mode === 'register' ? 'Register' : 'Create'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default FixedAssetForm;
